import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { formatDateTime } from '@/lib/i18n';
import { type Segment, type SegmentContactableResult, ContactChannel } from '@/Pages/CRM/lib';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  apiError,
  CampaignStatus,
  CampaignTriggerType,
  type Campaign,
  type CampaignSend,
  type CreateCampaignInput,
  MessagingChannel,
  type MessageTemplate,
} from './messagingLib';

const BLANK: CreateCampaignInput = {
  name: '',
  channel: MessagingChannel.EMAIL,
  templateId: '',
  triggerType: CampaignTriggerType.ONE_OFF,
  audienceSegmentId: '',
};

const CHANNEL_TO_CONTACT: Record<MessagingChannel, ContactChannel> = {
  [MessagingChannel.EMAIL]: ContactChannel.EMAIL,
  [MessagingChannel.SMS]: ContactChannel.SMS,
};

/**
 * Campaigns — one-off and triggered. Creating one is cheap (it starts in
 * DRAFT and sends nothing); activating it is the act this whole module
 * exists to gate, so the activate dialog is where the two things a manager
 * must see before real messages go out live: how many of the audience are
 * actually contactable on this channel (never assumed from segment size —
 * `GET /segments/:id/contactable`, the same check `SegmentMembersDialog`
 * already makes), and that quiet hours and the weekly fatigue cap apply
 * automatically to every send regardless.
 */
export default function CampaignsTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirmDialog = useConfirm();
  const canManage = can('campaigns.manage');
  const canSend = can('campaigns.send');
  const canSeeContactable = can('segments.view');

  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateCampaignInput>(BLANK);
  const [saving, setSaving] = useState(false);

  const [activating, setActivating] = useState<Campaign | null>(null);
  const [contactable, setContactable] = useState<SegmentContactableResult | null>(null);
  const [contactableLoading, setContactableLoading] = useState(false);
  const [activateSaving, setActivateSaving] = useState(false);

  const [sendsFor, setSendsFor] = useState<Campaign | null>(null);
  const [sends, setSends] = useState<CampaignSend[]>([]);
  const [sendsLoading, setSendsLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Campaign[] }>('/messaging/campaigns', {
        params: { limit: 100, status: status || undefined },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('messaging.campaigns.loadFailed'))))
      .finally(() => setLoading(false));
  }, [status, t]);

  useEffect(load, [load]);

  useEffect(() => {
    api
      .get<{ data: MessageTemplate[] }>('/messaging/templates', { params: { limit: 200, isActive: true } })
      .then(({ data }) => setTemplates(data.data ?? []))
      .catch(() => setTemplates([]));
    api
      .get<{ data: Segment[] }>('/segments', { params: { limit: 200 } })
      .then(({ data }) => setSegments(data.data ?? []))
      .catch(() => setSegments([]));
  }, []);

  const openCreate = () => {
    setForm(BLANK);
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/messaging/campaigns', form);
      toast.success(t('messaging.campaigns.created'));
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.campaigns.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  // Looked up locally rather than re-fetched: `segments` already carries
  // every segment's `neverEvaluated` flag, and this is the same fact
  // `CampaignsService.activate` 409s on server-side — checking it here lets
  // the dialog refuse up front instead of only after a failed POST.
  const activatingSegment = activating ? segments.find((s) => s.id === activating.audienceSegmentId) : undefined;

  const openActivate = async (campaign: Campaign) => {
    setActivating(campaign);
    setContactable(null);
    const segment = segments.find((s) => s.id === campaign.audienceSegmentId);
    if (segment?.neverEvaluated) return; // would 409 — see `activatingSegment` above
    if (!canSeeContactable) return;
    setContactableLoading(true);
    try {
      const { data } = await api.get<SegmentContactableResult>(
        `/segments/${campaign.audienceSegmentId}/contactable`,
        { params: { channel: CHANNEL_TO_CONTACT[campaign.channel] } },
      );
      setContactable(data);
    } catch {
      setContactable(null);
    } finally {
      setContactableLoading(false);
    }
  };

  const activate = async () => {
    if (!activating) return;
    setActivateSaving(true);
    try {
      await api.post(`/messaging/campaigns/${activating.id}/activate`);
      toast.success(t('messaging.campaigns.activated'));
      setActivating(null);
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.campaigns.activateFailed')));
    } finally {
      setActivateSaving(false);
    }
  };

  const pause = async (campaign: Campaign) => {
    try {
      await api.post(`/messaging/campaigns/${campaign.id}/pause`);
      toast.success(t('messaging.campaigns.paused'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.campaigns.actionFailed')));
    }
  };

  const cancel = async (campaign: Campaign) => {
    const ok = await confirmDialog.confirm({
      title: t('messaging.campaigns.cancelConfirmTitle', { name: campaign.name }),
      body: t('messaging.campaigns.cancelConfirmBody'),
      confirmLabel: t('messaging.campaigns.cancel'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/messaging/campaigns/${campaign.id}/cancel`);
      toast.success(t('messaging.campaigns.cancelled'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.campaigns.actionFailed')));
    }
  };

  const openSends = async (campaign: Campaign) => {
    setSendsFor(campaign);
    setSendsLoading(true);
    try {
      const { data } = await api.get<{ data: CampaignSend[] }>(`/messaging/campaigns/${campaign.id}/sends`, {
        params: { limit: 100 },
      });
      setSends(data.data ?? []);
    } catch (e) {
      toast.error(apiError(e, t('messaging.campaigns.sendsLoadFailed')));
    } finally {
      setSendsLoading(false);
    }
  };

  const templatesForChannel = templates.filter((tpl) => tpl.channel === form.channel);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <Field label={t('common.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as CampaignStatus | '')}>
              <option value="">{t('messaging.campaigns.anyStatus')}</option>
              {Object.values(CampaignStatus).map((s) => (
                <option key={s} value={s}>
                  {t(`messaging.campaignStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          {canManage && (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('messaging.campaigns.newCampaign')}
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('messaging.campaigns.empty')}
          hint={t('messaging.campaigns.emptyHint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                {t('messaging.campaigns.newCampaign')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('messaging.campaigns.name')}</TH>
                <TH>{t('messaging.templates.channel')}</TH>
                <TH>{t('messaging.campaigns.trigger')}</TH>
                <TH>{t('common.status')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((c) => (
                <TR key={c.id}>
                  <TD className="font-medium">{c.name}</TD>
                  <TD>{t(`crm.consent.channel.${c.channel}`)}</TD>
                  <TD>{t(`messaging.campaignTrigger.${c.triggerType}`)}</TD>
                  <TD>
                    <Badge tone={toneForStatus(c.status)}>{t(`messaging.campaignStatus.${c.status}`)}</Badge>
                  </TD>
                  <TD className="text-end">
                    <div className="flex justify-end gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => void openSends(c)}>
                        {t('messaging.campaigns.sends')}
                      </Button>
                      {canSend && (c.status === CampaignStatus.DRAFT || c.status === CampaignStatus.PAUSED) && (
                        <Button size="sm" onClick={() => void openActivate(c)}>
                          {t('messaging.campaigns.activate')}
                        </Button>
                      )}
                      {canSend && (c.status === CampaignStatus.SCHEDULED || c.status === CampaignStatus.ACTIVE) && (
                        <Button variant="outline" size="sm" onClick={() => void pause(c)}>
                          {t('messaging.campaigns.pause')}
                        </Button>
                      )}
                      {canSend && c.status !== CampaignStatus.COMPLETED && c.status !== CampaignStatus.CANCELLED && (
                        <Button variant="ghost" size="sm" onClick={() => void cancel(c)}>
                          {t('messaging.campaigns.cancel')}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('messaging.campaigns.newCampaign')}
        className="max-w-lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={saving || !form.name || !form.templateId || !form.audienceSegmentId}
              onClick={() => void submit()}
            >
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label={t('messaging.campaigns.name')}>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label={t('messaging.campaigns.description')}>
            <Textarea
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('messaging.templates.channel')}>
              <Select
                value={form.channel}
                onChange={(e) =>
                  setForm((f) => ({ ...f, channel: e.target.value as MessagingChannel, templateId: '' }))
                }
              >
                {Object.values(MessagingChannel).map((c) => (
                  <option key={c} value={c}>
                    {t(`crm.consent.channel.${c}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('messaging.campaigns.trigger')}>
              <Select
                value={form.triggerType}
                onChange={(e) => setForm((f) => ({ ...f, triggerType: e.target.value as CampaignTriggerType }))}
              >
                {Object.values(CampaignTriggerType).map((tt) => (
                  <option key={tt} value={tt}>
                    {t(`messaging.campaignTrigger.${tt}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={t('messaging.campaigns.template')} hint={t('messaging.campaigns.templateHint')}>
            <Select value={form.templateId} onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}>
              <option value="">{t('messaging.campaigns.pickTemplate')}</option>
              {templatesForChannel.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.key} ({tpl.locale})
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('messaging.campaigns.audience')} hint={t('messaging.campaigns.audienceHint')}>
            <Select
              value={form.audienceSegmentId}
              onChange={(e) => setForm((f) => ({ ...f, audienceSegmentId: e.target.value }))}
            >
              <option value="">{t('messaging.campaigns.pickSegment')}</option>
              {segments.map((seg) => (
                <option key={seg.id} value={seg.id}>
                  {/* "(0)" and "never evaluated" are different facts — a
                      segment that has never run cannot activate a campaign
                      (`CampaignsService.activate` 409s on it), and showing
                      plain "(0)" here would read exactly like a segment
                      genuinely evaluated to zero members. */}
                  {seg.name} (
                  {seg.neverEvaluated ? t('crm.segments.neverEvaluatedBadge') : seg.memberCount})
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.repeatable ?? false}
              onChange={(e) => setForm((f) => ({ ...f, repeatable: e.target.checked }))}
              className="h-4 w-4"
            />
            {t('messaging.campaigns.repeatable')}
          </label>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(activating)}
        onClose={() => setActivating(null)}
        title={t('messaging.campaigns.activateConfirmTitle', { name: activating?.name ?? '' })}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setActivating(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={activateSaving || activatingSegment?.neverEvaluated}
              onClick={() => void activate()}
            >
              {t('messaging.campaigns.activate')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          {activatingSegment?.neverEvaluated ? (
            <div className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{t('crm.segments.neverEvaluatedTitle')}</p>
                <p className="mt-0.5">{t('messaging.campaigns.segmentNeverEvaluatedBlock')}</p>
              </div>
            </div>
          ) : contactableLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : contactable ? (
            <div className="rounded-lg border p-3">
              <p className="font-medium">
                {t('messaging.campaigns.contactableSummary', {
                  contactable: contactable.contactableCount,
                  total: contactable.memberCount,
                })}
              </p>
              {contactable.contactableCount < contactable.memberCount && (
                <p className="mt-1 flex items-start gap-1.5 text-amber-700">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  {t('messaging.campaigns.contactableGapHint')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-ink-muted">{t('messaging.campaigns.contactableUnavailable')}</p>
          )}
          <p className="text-ink-muted">{t('messaging.campaigns.activateNote')}</p>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(sendsFor)}
        onClose={() => setSendsFor(null)}
        title={t('messaging.campaigns.sendsTitle', { name: sendsFor?.name ?? '' })}
        className="max-w-2xl"
        footer={
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setSendsFor(null)}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        {sendsLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : sends.length === 0 ? (
          <EmptyState title={t('messaging.campaigns.noSends')} hint={t('messaging.campaigns.noSendsHint')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('messaging.campaigns.sentAt')}</TH>
                  <TH>{t('common.status')}</TH>
                </TR>
              </THead>
              <tbody>
                {sends.map((s) => (
                  <TR key={s.id}>
                    <TD>{formatDateTime(s.createdAt, i18n.language)}</TD>
                    <TD>
                      {/* `toneForStatus` (badge.tsx) now knows every
                          `CampaignSendStatus` member — a consent skip or a
                          fatigue-cap skip is the gate working as designed,
                          not a failure, and reads `neutral` rather than the
                          `warning` this used to lump every SKIPPED_* reason
                          into. */}
                      <Badge tone={toneForStatus(s.status)}>{t(`messaging.sendStatus.${s.status}`)}</Badge>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Dialog>
    </div>
  );
}
